import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  toast,
  Text,
  Tooltip,
  Dialog,
  DialogContent,
  IconButton,
  Input,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FormLabel
} from '@sparrowengg/twigs-react';
import {
  SaveIcon,
  DeleteIcon,
  EllipsisVerticalIcon,
  CloseIcon,
  CopyIcon
} from '@sparrowengg/twigs-react-icons';
import FileUploads from './file-uploads';
import AppDetails from './app-details';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { defaultValues, youtubeRegex, appTypeConstants } from '../constants';
import { devPortalServices } from 'commons/services';
import { AddLocalDevAppProps } from '../types';
import Cookies from 'universal-cookie';
import { jwtDecode } from 'jwt-decode';
import { authenticateUserValues } from 'commons/constants';
import DocumentationBanner from 'commons/components/documentation-banner';

const AddLocalDevApp = ({ app: appProp }: AddLocalDevAppProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appIdFromQuery = searchParams.get('appId');
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    watch,
    reset,
    getValues
  } = useForm({
    defaultValues
  });
  const [loading, setLoading] = useState(false);
  const [disabled, setIsDisabled] = useState({ publish: true, update: false });
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBtnLoader, setDeleteBtnLoader] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configToken, setConfigToken] = useState<string>('');
  const [configLoading, setConfigLoading] = useState(false);
  const [app, setApp] = useState(appProp);
  const [currentAppId, setCurrentAppId] = useState<number | null>(
    appProp?.appId || (appIdFromQuery ? parseInt(appIdFromQuery, 10) : null)
  );

  const convertUrl = (url: string): string => {
    if (youtubeRegex.test(url)) {
      const videoIdMatch = url.match(youtubeRegex);
      const videoId = videoIdMatch ? videoIdMatch[4] : null;
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    return url;
  };

  const isEmptyObject = (obj: any): boolean => {
    if (!obj || typeof obj !== 'object') return true;
    return Object.keys(obj).length === 0;
  };

  const prepareFormData = (value: any, isUpdate: boolean = false) => {
    const formData = new FormData();

    // Use default video URL if not provided
    const videoUrl =
      value.videoUrl?.trim() || 'https://www.youtube.com/watch?v=RymrDTbydWc';
    value.videoUrl = convertUrl(videoUrl);

    // Handle all fields
    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        if (key === 'events') {
          // Handle events as array
          if (Array.isArray(value[key]) && value[key].length > 0) {
            value[key].forEach((event: string) => {
              formData.append('events[]', event);
            });
          }
        } else if (key === 'location') {
          // Handle location as locationName
          if (value[key]) {
            formData.append('locationName', value[key]);
          }
        } else if (key === 'iParams') {
          // Handle iParams as JSON string - only send if not empty
          const iParams = value[key];
          if (iParams) {
            if (typeof iParams === 'string' && iParams.trim()) {
              try {
                const parsed = JSON.parse(iParams);
                if (!isEmptyObject(parsed)) {
                  formData.append('iParams', JSON.stringify(parsed));
                }
              } catch {
                // If parsing fails, send as is if not empty
                if (iParams.trim()) {
                  formData.append('iParams', iParams);
                }
              }
            } else if (typeof iParams === 'object' && !isEmptyObject(iParams)) {
              formData.append('iParams', JSON.stringify(iParams));
            }
          }
        } else if (key === 'oauth') {
          // Handle oauth as JSON string - only send if not empty
          const oauth = value[key];
          if (oauth) {
            if (typeof oauth === 'string' && oauth.trim()) {
              try {
                const parsed = JSON.parse(oauth);
                if (!isEmptyObject(parsed)) {
                  formData.append('oauth', JSON.stringify(parsed));
                }
              } catch {
                // If parsing fails, send as is if not empty
                if (oauth.trim()) {
                  formData.append('oauth', oauth);
                }
              }
            } else if (typeof oauth === 'object' && !isEmptyObject(oauth)) {
              formData.append('oauth', JSON.stringify(oauth));
            }
          }
        } else if (key === 'whitelistedDomains') {
          // Handle whitelistedDomains as array - ensure it's a simple array of trimmed strings
          let domains = value[key];

          // If domains is a string, try to parse it
          if (typeof domains === 'string') {
            try {
              domains = JSON.parse(domains);
            } catch {
              // If parsing fails, treat as single domain
              domains = [domains];
            }
          }

          // Ensure it's an array and process each domain
          if (Array.isArray(domains) && domains.length > 0) {
            // Filter out empty strings, trim each domain, and remove duplicates
            const processedDomains = domains
              .map((domain: any) => {
                // If domain is a stringified array, parse it
                if (
                  typeof domain === 'string' &&
                  domain.trim().startsWith('[')
                ) {
                  try {
                    const parsed = JSON.parse(domain);
                    return Array.isArray(parsed) ? parsed : [domain];
                  } catch {
                    return domain.trim();
                  }
                }
                return typeof domain === 'string'
                  ? domain.trim()
                  : String(domain).trim();
              })
              .flat() // Flatten in case we had nested arrays
              .filter((domain: string) => domain.length > 0) // Remove empty strings
              .filter(
                (domain: string, index: number, self: string[]) =>
                  self.indexOf(domain) === index
              ); // Remove duplicates

            // Append each domain as a separate entry (not stringified)
            processedDomains.forEach((domain: string) => {
              formData.append('whitelistedDomains[]', domain);
            });
          }
        } else if (key === 'icon') {
          // Handle icon - only send if it's a new file (File/Blob), not a URL string
          if (value[key]) {
            if (value[key] instanceof File) {
              formData.append(key, value[key]);
            } else if (value[key]?.data) {
              const iconData = value[key].data;
              // Only append if it's a File or Blob, not a URL string
              if (iconData instanceof File || iconData instanceof Blob) {
                formData.append(key, iconData);
              }
              // Skip if iconData is a string (URL) - don't send unchanged icon
            }
          }
        } else {
          formData.append(key, value[key]);
        }
      }
    }

    // For updates, only send icon if it's a new file (File/Blob), not the existing URL
    if (isUpdate) {
      const icon = getValues('icon');
      // Only append icon if it exists, wasn't already added in the loop above, and is a File/Blob
      if (icon && !value.icon) {
        if (icon instanceof File) {
          formData.append('icon', icon);
        } else if (icon && typeof icon === 'object' && 'data' in icon) {
          const iconData = (icon as any).data;
          // Only append if it's a File or Blob, not a URL string
          if (iconData instanceof File || iconData instanceof Blob) {
            formData.append('icon', iconData);
          }
          // Skip if iconData is a string (URL) - don't send unchanged icon
        }
      }
    }

    return formData;
  };

  const submitHandler = async (value: any) => {
    setLoading(true);
    try {
      const appIdToUse = currentAppId || app?.appId;

      if (appIdToUse) {
        // Update existing app
        const formData = prepareFormData(value, true);
        formData.append('appVersionId', app?.appVersionId?.toString() || '');
        formData.append('appId', appIdToUse.toString());

        await devPortalServices.updateLocalDevApp(formData);

        toast({
          variant: 'success',
          description: 'Local dev app has been updated successfully'
        });
        navigate('/');
      } else {
        // Create new app
        const formData = prepareFormData(value, false);
        const response = await devPortalServices.uploadLocalDevApp(formData);

        if (response?.appId) {
          setCurrentAppId(response.appId);
          setApp(response);
          toast({
            variant: 'success',
            description: 'Local dev app has been created successfully'
          });
          // Don't navigate, stay on the page for further editing
        } else {
          toast({
            variant: 'success',
            description: 'Local dev app has been uploaded successfully'
          });
          navigate('/');
        }
      }
    } catch (err) {
      console.error(err);
      toast({
        variant: 'error',
        description: 'Error in file upload, Please try again'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApp = async () => {
    setDeleteBtnLoader(true);
    try {
      const appIdToDelete = currentAppId || app?.appId;
      if (appIdToDelete) {
        await devPortalServices.deleteLocalDevApp(appIdToDelete);
        navigate('/');
        toast({
          variant: 'default',
          description: `${app?.name || 'App'} has been deleted successfully.`
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteBtnLoader(false);
    }
  };

  const handleDisableButton = useCallback(async () => {
    try {
      const appIdToVerify = currentAppId || app?.appId;
      if (appIdToVerify) {
        const response = await devPortalServices.verifyDeleteApp(
          appIdToVerify as number
        );
        setDeleteButtonDisabled(!response);
        setIsDisabled({ publish: false, update: false });
      }
    } catch (err) {
      console.error(err);
    }
  }, [currentAppId, app?.appId]);

  const handleGenerateConfiguration = async () => {
    setConfigLoading(true);
    try {
      const cookie = new Cookies();
      const token = cookie.get(authenticateUserValues.TOKEN);
      if (!token) {
        toast({
          variant: 'error',
          description: 'Authentication token not found'
        });
        return;
      }
      const decodedToken: any = jwtDecode(token);
      const productKey = decodedToken?.productKey;

      if (!productKey) {
        toast({
          variant: 'error',
          description: 'Product key not found in token'
        });
        return;
      }

      const appIdToUse = currentAppId || app?.appId;
      if (!appIdToUse) {
        toast({
          variant: 'error',
          description: 'App ID not found'
        });
        return;
      }

      const response = await devPortalServices.authenticateMCP(
        productKey,
        appIdToUse
      );
      // Extract Access_Token from response
      const mcpToken =
        response?.Access_Token || response?.data?.Access_Token || '';
      if (!mcpToken) {
        toast({
          variant: 'error',
          description: 'Failed to generate token. Please try again.'
        });
        return;
      }
      setConfigToken(mcpToken);
      setShowConfigModal(true);
    } catch (err) {
      console.error(err);
      toast({
        variant: 'error',
        description: 'Error generating configuration. Please try again.'
      });
    } finally {
      setConfigLoading(false);
    }
  };

  const copyConfigurationToClipboard = () => {
    const config = {
      mcpServers: {
        'local-twigs-ai-mcp': {
          command: 'node',
          args: [
            '/Users/aravinthanp/Desktop/Marketplace/new-structure/appnest-ai-mcp/build/server.js'
          ],
          cwd: '/Users/aravinthanp/Desktop/Marketplace/new-structure/appnest-ai-mcp/build/',
          transport: 'stdio',
          env: {
            'appnest-token': configToken
          },
          allowedExecutables: [
            'git',
            'ssdk',
            'node',
            'cd',
            'echo',
            'npm',
            'mkdir'
          ]
        }
      }
    };
    navigator.clipboard.writeText(JSON.stringify(config, null, 2));
    toast({
      variant: 'success',
      description: 'Configuration copied to clipboard'
    });
  };

  // Fetch app if appId is in query params (only if appProp is not provided)
  // Note: If appProp is provided, it means EditLocalDevApp already fetched it
  useEffect(() => {
    const fetchApp = async () => {
      if (appIdFromQuery && !appProp && !app) {
        try {
          const response = await devPortalServices.getSingleApp(appIdFromQuery);
          const convertToYouTubeURL = (embedUrl: string): string => {
            const embedRegex = /\/embed\/([^/?]+)/;
            const match = embedUrl?.match(embedRegex);
            if (match && match[1]) {
              return `https://www.youtube.com/watch?v=${match[1]}`;
            } else {
              return embedUrl;
            }
          };
          response.videoUrl = convertToYouTubeURL(response?.videoUrl);
          setApp(response);
          setCurrentAppId(response.appId);
        } catch (err) {
          console.error('Error fetching app:', err);
        }
      }
    };

    fetchApp();
  }, [appIdFromQuery, appProp, app]);

  // Set default values for new app creation (not for editing)
  useEffect(() => {
    const setDefaultsForNewApp = async () => {
      // Only set defaults when creating a new app (not editing)
      if (!app && !appIdFromQuery && !appProp) {
        try {
          // Fetch locations and set default
          const locationsResponse = await devPortalServices.getAllLocations();
          if (
            locationsResponse?.data &&
            Array.isArray(locationsResponse.data)
          ) {
            // Find "full_page_app" location
            const fullPageAppLocation = locationsResponse.data.find(
              (loc: any) => loc.name === 'full_page_app'
            );
            if (fullPageAppLocation) {
              setValue('location', 'full_page_app', { shouldDirty: false });
            }
          }
        } catch (err) {
          console.error('Error setting defaults for new app:', err);
        }
      }
    };

    setDefaultsForNewApp();
  }, [app, appIdFromQuery, appProp, setValue]);

  // Update form when app data is available
  useEffect(() => {
    if (app) {
      const {
        name,
        description,
        howToUse,
        icon,
        videoUrl,
        websiteLink,
        appOverView,
        supportEmail,
        supportUrl
      } = app;

      // Extract location from locations array or location.data
      const extractLocation = () => {
        const appAny = app as any;
        // Check if locations is an array of strings
        if (Array.isArray(appAny.locations) && appAny.locations.length > 0) {
          return appAny.locations[0];
        }
        // Check if location.data exists and has items
        if (
          appAny.location?.data &&
          Array.isArray(appAny.location.data) &&
          appAny.location.data.length > 0
        ) {
          return appAny.location.data[0].name;
        }
        // Fallback to locationName or location string
        return appAny.locationName || appAny.location || '';
      };

      // Extract events from events.data structure
      const extractEvents = () => {
        const appAny = app as any;
        if (appAny.events?.data && Array.isArray(appAny.events.data)) {
          // Flatten the nested structure: events.data[].events[].name
          const eventNames: string[] = [];
          appAny.events.data.forEach((category: any) => {
            if (category.events && Array.isArray(category.events)) {
              category.events.forEach((event: any) => {
                if (event.name) {
                  eventNames.push(event.name);
                }
              });
            }
          });
          return eventNames;
        }
        // Fallback to events array if it's already an array
        if (Array.isArray(appAny.events)) {
          return appAny.events;
        }
        return [];
      };

      const formValues = {
        appName: name,
        shortDescription: description,
        appInstructions: howToUse,
        icon: { data: icon },
        videoUrl,
        supportUrl: supportUrl || '',
        appOverView,
        supportEmail,
        websiteLink: websiteLink || '',
        ngrokUrl: (app as any)?.ngrokUrl || '',
        location: extractLocation(),
        events: extractEvents(),
        iParams: (() => {
          const appAny = app as any;
          // Check for iparams (lowercase) first, then iParams (camelCase)
          const iParams = appAny.iparams || appAny.iParams;
          if (typeof iParams === 'string') {
            try {
              return JSON.parse(iParams);
            } catch {
              return {};
            }
          }
          // If it's already an object, return it directly
          if (iParams && typeof iParams === 'object') {
            return iParams;
          }
          return {};
        })(),
        oauth: (() => {
          const appAny = app as any;
          // Check for oauth (lowercase) first, then oAuth (camelCase)
          const oauth = appAny.oauth || appAny.oAuth;
          if (typeof oauth === 'string') {
            try {
              return JSON.parse(oauth);
            } catch {
              return {};
            }
          }
          // If it's already an object, return it directly
          if (oauth && typeof oauth === 'object') {
            return oauth;
          }
          return {};
        })(),
        whitelistedDomains: (() => {
          const appAny = app as any;
          // Check for whitelistedDomains or whitelisted_domains
          const domains =
            appAny.whitelistedDomains || appAny.whitelisted_domains;
          if (Array.isArray(domains)) {
            return domains;
          }
          if (typeof domains === 'string') {
            try {
              const parsed = JSON.parse(domains);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        })()
      };
      reset({
        ...formValues
      });
      // Only call handleDisableButton once when app is first loaded
      if (app && (currentAppId || app?.appId)) {
        handleDisableButton();
      }
    } else if (!appIdFromQuery && !appProp) {
      reset(defaultValues);
    }
  }, [app, appIdFromQuery, appProp, currentAppId, handleDisableButton]);

  useEffect(() => {
    if ((app || currentAppId) && !isDirty) {
      setIsDisabled({ publish: false, update: true });
    } else {
      setIsDisabled((prev) => ({ ...prev, update: false }));
    }
  }, [isDirty, app, currentAppId]);

  return (
    <Flex
      justifyContent="center"
      css={{ marginTop: '$24', paddingBottom: '$20' }}
    >
      <Box css={{ maxWidth: 1200, width: '100%', padding: '$0 $20' }}>
        <DocumentationBanner />
        <form onSubmit={handleSubmit(submitHandler)}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            css={{
              paddingBlock: '$5'
            }}
          >
            <Flex flexDirection="column" gap="$2">
              <Heading size="h5">Local Dev App Information</Heading>
              <Text size="md" css={{ color: '$neutral800' }}>
                Enter all the required fields to{' '}
                {app || currentAppId ? 'update' : 'upload'} your local
                development app
              </Text>
            </Flex>
            <Flex alignItems="center" gap="$4">
              {(app || currentAppId) && (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={handleGenerateConfiguration}
                  loading={configLoading}
                  css={{ marginRight: '$4' }}
                >
                  Generate MCP Configuration
                </Button>
              )}
              <Button
                size="lg"
                type="submit"
                loading={loading}
                disabled={disabled.publish || disabled.update}
                rightIcon={<SaveIcon />}
                css={{ marginRight: '$4' }}
              >
                {app || currentAppId ? 'Update Changes' : 'Save Changes'}
              </Button>
              <Box
                css={{
                  borderLeft: '$borderWidths$xs solid $neutral200',
                  height: '$6',
                  width: '1px'
                }}
              />
              {app || currentAppId ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      icon={<EllipsisVerticalIcon />}
                      size="lg"
                      variant="ghost"
                      color="default"
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    css={{ minWidth: 200 }}
                    align="end"
                    sideOffset={4}
                  >
                    <Tooltip
                      content="See the App feature usage"
                      size="md"
                      side="right"
                    >
                      <Flex css={{ width: '100%' }}>
                        <DropdownMenuItem css={{ width: '100%' }}>
                          <Flex
                            alignItems="center"
                            gap="$4"
                            onClick={() => {
                              const appIdToUse = currentAppId || app?.appId;
                              if (appIdToUse) {
                                navigate(
                                  `/edit-local-dev-app/${appIdToUse}/appFeatureUsage`
                                );
                              }
                            }}
                            css={{ width: '100%', cursor: 'pointer' }}
                          >
                            <Box
                              css={{
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4 7C4 5.89543 4.89543 5 6 5H8.58579C8.851 5 9.10536 5.10536 9.29289 5.29289L11.7071 7.70711C11.8946 7.89464 12.149 8 12.4142 8H18C19.1046 8 20 8.89543 20 10V17C20 18.1046 19.1046 19 18 19H6C4.89543 19 4 18.1046 4 17V7Z"
                                  stroke="#9a9a9a"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M8 12H16M8 15H12"
                                  stroke="#9a9a9a"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </Box>
                            App Feature Usage
                          </Flex>
                        </DropdownMenuItem>
                      </Flex>
                    </Tooltip>
                    <Tooltip
                      hidden={!deleteButtonDisabled}
                      content="App installed in one or more accounts cannot be deleted. Please contact support for more details."
                      size="md"
                      side="right"
                    >
                      <Flex css={{ width: '100%' }}>
                        <DropdownMenuItem
                          css={{ width: '100%' }}
                          disabled={deleteButtonDisabled}
                        >
                          <Flex
                            alignItems="center"
                            gap="$4"
                            onClick={() => setShowDeleteModal(true)}
                            css={{ width: '100%', cursor: 'pointer' }}
                          >
                            <DeleteIcon size={24} color="#6a6a6a" />
                            Delete App
                          </Flex>
                        </DropdownMenuItem>
                      </Flex>
                    </Tooltip>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </Flex>
          </Flex>
          <Flex css={{ paddingBlock: '$20' }}>
            <FileUploads
              getValues={getValues}
              setValue={setValue}
              watch={watch}
            />
            <Box css={{ flex: 1 }}>
              <AppDetails
                app={app}
                register={register as any}
                errors={errors as any}
                watch={watch as any}
                isDirty={isDirty}
                setValue={setValue as any}
                getValues={getValues as any}
                setIsDisabled={setIsDisabled}
                setDeleteButtonDisabled={setDeleteButtonDisabled}
              />
            </Box>
          </Flex>
        </form>
      </Box>
      {showDeleteModal && (
        <WarningModal
          resetModal={() => {
            setShowDeleteModal(false);
          }}
          btnLoader={deleteBtnLoader}
          onClickHandler={() => handleDeleteApp()}
          appName={app?.name ?? ''}
        />
      )}
      {showConfigModal && (
        <ConfigurationModal
          resetModal={() => {
            setShowConfigModal(false);
            setConfigToken('');
          }}
          configToken={configToken}
          onCopy={copyConfigurationToClipboard}
        />
      )}
    </Flex>
  );
};

const WarningModal = ({
  resetModal,
  btnLoader,
  onClickHandler,
  appName
}: {
  resetModal: () => void;
  btnLoader?: boolean;
  onClickHandler: () => void;
  appName: string;
}) => {
  const [isDisabled, setIsDisabled] = useState(true);

  return (
    <Dialog open>
      <DialogContent css={{ padding: 0, borderRadius: '$xl', width: 476 }}>
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">App Deletion Confirmation</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={() => resetModal()}
          />
        </Flex>
        <Flex flexDirection="column" gap="$14" css={{ padding: '$12' }}>
          <Flex flexDirection="column" gap="$4">
            <Text size="sm">
              To delete, type the app name{' '}
              <Text
                as="span"
                size="sm"
                css={{ color: '$secondary400', whiteSpace: 'break-spaces' }}
                weight="bold"
              >
                {appName}
              </Text>
            </Text>
            <Input
              size="lg"
              onChange={(e) => {
                e.preventDefault();
                const inputValue = e?.currentTarget?.value?.trim() || '';
                const appNameNormalized = appName?.trim() || '';
                if (inputValue === appNameNormalized) {
                  setIsDisabled(false);
                } else {
                  setIsDisabled(true);
                }
              }}
            />
            <FormLabel>The app name must be case-sensitive</FormLabel>
          </Flex>
          <Text size="md">
            <Box as="strong">Note:</Box> The deletion will remove all the App
            history and it can't be revived.
          </Text>
          <Text size="md">Are you sure you want to delete the app?</Text>
        </Flex>
        <Flex
          alignItems="center"
          gap="$6"
          css={{
            padding: '$12',
            paddingTop: 0,
            button: {
              flex: 1
            }
          }}
        >
          <Button
            color="error"
            size="xl"
            onClick={() => resetModal()}
            disabled={btnLoader}
          >
            No, Go Back
          </Button>
          <Button
            color="secondary"
            size="xl"
            onClick={() => onClickHandler()}
            disabled={isDisabled || btnLoader}
            loading={btnLoader}
          >
            Yes, I am sure
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

const ConfigurationModal = ({
  resetModal,
  configToken,
  onCopy
}: {
  resetModal: () => void;
  configToken: string;
  onCopy: () => void;
}) => {
  const config = {
    mcpServers: {
      'local-twigs-ai-mcp': {
        command: 'node',
        args: [
          '/Users/aravinthanp/Desktop/Marketplace/new-structure/appnest-ai-mcp/build/server.js'
        ],
        cwd: '/Users/aravinthanp/Desktop/Marketplace/new-structure/appnest-ai-mcp/build/',
        transport: 'stdio',
        'appnest-token': configToken,
        allowedExecutables: [
          'git',
          'ssdk',
          'node',
          'cd',
          'echo',
          'npm',
          'mkdir'
        ]
      }
    }
  };

  const configString = JSON.stringify(config, null, 2);

  return (
    <Dialog open>
      <DialogContent
        css={{ padding: 0, borderRadius: '$xl', width: 600, maxWidth: '90vw' }}
      >
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">Generate MCP Configuration</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={resetModal}
          />
        </Flex>
        <Flex flexDirection="column" gap="$6" css={{ padding: '$12' }}>
          <Text size="md" css={{ color: '$neutral800', lineHeight: '$md' }}>
            Copy the configuration and paste it in your{' '}
            <Box
              as="span"
              css={{
                fontFamily: 'monospace',
                backgroundColor: '$neutral100',
                padding: '$1 $2',
                borderRadius: '$sm',
                fontSize: '$sm'
              }}
            >
              .mcp
            </Box>{' '}
            file in the project scope level
          </Text>
          <Box
            css={{
              position: 'relative',
              backgroundColor: '$neutral50',
              border: '$borderWidths$xs solid $neutral200',
              borderRadius: '$md',
              padding: '$8',
              fontFamily: 'monospace',
              fontSize: '$sm',
              overflow: 'auto',
              maxHeight: '400px',
              lineHeight: '$md'
            }}
          >
            <Box
              as="pre"
              css={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '$neutral900',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit'
              }}
            >
              {configString}
            </Box>
            <IconButton
              icon={<CopyIcon />}
              size="sm"
              variant="ghost"
              color="default"
              onClick={onCopy}
              css={{
                position: 'absolute',
                top: '$4',
                right: '$4',
                backgroundColor: '$white900',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  backgroundColor: '$neutral100'
                }
              }}
            />
          </Box>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="flex-end"
          css={{
            padding: '$12',
            paddingTop: 0,
            gap: '$4'
          }}
        >
          <Button size="lg" variant="outline" onClick={resetModal}>
            Close
          </Button>
          <Button
            size="lg"
            color="secondary"
            onClick={onCopy}
            rightIcon={<CopyIcon />}
          >
            Copy Configuration
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

export default AddLocalDevApp;
